import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'

import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth } from '@/lib/api'
import { phoneLineKeys } from '@/lib/queryKeys'
import { QUERY_GC_TIME_MS, QUERY_STALE_TIME_MS } from '@/config/constants'
import { useIsCareforceOrgAdmin } from '@/hooks/useIsCareforceOrgAdmin'
import type { PhoneLineSummary } from '@/features/phone-lines/types'

/**
 * Card metrics for the global phone-line pool. Super-user-only (the endpoint
 * 403s otherwise), so the query is disabled for everyone else. Polled to stay
 * fresh, matching the live nature of the pool.
 *
 * Gated on `useIsCareforceOrgAdmin` rather than `useIsSuperUser` so the card
 * tracks the board it links to: the pool is Careforce-wide, so advertising a
 * live count from a customer org would link to a page that can't render. The
 * endpoint itself still works from any org (hoku reads it service-role), so this
 * is a product boundary, not a technical one.
 */
export function usePhoneLineSummaryQuery() {
  const { getToken, userId } = useAuth()
  const isCareforceOrgAdmin = useIsCareforceOrgAdmin()

  return useQuery({
    queryKey: phoneLineKeys.summary(userId),
    queryFn: async () => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchJsonWithAuth<PhoneLineSummary>({
        token,
        url: `${AGENT_URL}/phone-lines/summary`,
        errorMessage: 'Failed to fetch phone-line summary',
      })
    },
    enabled: isCareforceOrgAdmin,
    staleTime: QUERY_STALE_TIME_MS,
    refetchInterval: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}
